/**
 * Servizi API per TaskProject (BE sezione 6.1).
 *
 * Endpoint:
 *   GET/POST/PATCH  /api/tasks/projects/
 *   GET             /api/tasks/projects/{id}/
 *   POST            /api/tasks/projects/{id}/archive/
 *   POST            /api/tasks/projects/{id}/restore/
 *
 * Tutte le response usano la shape autosufficiente definita in `types/tasks.ts`:
 * ogni progetto include `task_count`; il detail include anche `columns` ordinate.
 */

import api from "../api/axios";
import type {
  TaskProject,
  CreateProjectPayload,
  UpdateProjectPayload,
} from "../types/tasks";

const BASE_URL = "api/tasks/projects";

const buildQuery = (params: Record<string, unknown> = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === "boolean") {
      q.append(k, v ? "true" : "false");
    } else {
      q.append(k, String(v));
    }
  });
  return q.toString();
};

/**
 * Lista progetti del tenant, ordinata per position/name/id (BE 6.1).
 * Non paginata nella prima release.
 *
 * @param params.archived Filtra per stato archivio. Default: `false` (solo attivi).
 *                        Passare `undefined` per ottenere tutti (attivi + archiviati).
 * @param params.search   Filtro testuale case-insensitive su nome/descrizione.
 */
export const getTaskProjects = (
  params: { archived?: boolean; search?: string } = {}
): Promise<{ data: TaskProject[] }> => {
  const qs = buildQuery(params as Record<string, unknown>);
  return api.get(`${BASE_URL}/${qs ? `?${qs}` : ""}`);
};

/** Dettaglio progetto con `columns` ordinate per order,id. */
export const getTaskProject = (id: number): Promise<{ data: TaskProject }> => {
  return api.get(`${BASE_URL}/${id}/`);
};

/**
 * Crea un progetto. Il payload accetta `columns` inline (modalità base/personalizzata)
 * oppure `copy_columns_from_project_id` (mutuamente esclusivi). Se entrambi mancano,
 * il backend crea la struttura base [Da fare, In corso, Completato].
 *
 * Transazione atomica: in caso di errore su una colonna, nessun progetto viene creato.
 */
export const createTaskProject = (
  payload: CreateProjectPayload
): Promise<{ data: TaskProject }> => {
  return api.post(`${BASE_URL}/`, payload);
};

/**
 * Aggiorna solo i metadati del progetto (nome, descrizione, colore, icona).
 * Le colonne si gestiscono via /task-statuses/. Le modifiche sono vietate
 * su progetto archiviato (backend risponde 409 project_archived).
 */
export const updateTaskProject = (
  id: number,
  payload: UpdateProjectPayload
): Promise<{ data: TaskProject }> => {
  return api.patch(`${BASE_URL}/${id}/`, payload);
};

/** Archivia il progetto (idempotente). Task/colonne non vengono toccati. */
export const archiveTaskProject = (
  id: number
): Promise<{ data: TaskProject }> => {
  return api.post(`${BASE_URL}/${id}/archive/`);
};

/** Ripristina un progetto archiviato (idempotente). Azzera archived_at. */
export const restoreTaskProject = (
  id: number
): Promise<{ data: TaskProject }> => {
  return api.post(`${BASE_URL}/${id}/restore/`);
};
